import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { patientApi, patientErr } from "@/lib/patientApi";
import { Heart, EnvelopeSimple, LockKey, User, ArrowLeft } from "@phosphor-icons/react";

const inputCls = "w-full rounded-xl border border-pink-100 bg-[#FDFBFE] px-4 py-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#EC4899] focus:bg-white focus:ring-2 focus:ring-[#EC4899]/15";
const btnCls = "flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#7C4DE0] to-[#DB2777] px-5 py-3.5 font-heading text-base font-extrabold text-white shadow-md transition-transform active:scale-[0.99] disabled:opacity-60";

function Shell({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-[#FAF7FF]">
      <header className="border-b border-rose-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <a href="/login" data-testid="patient-home" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><ArrowLeft size={16} weight="bold" /> Beranda</a>
          <span className="flex items-center gap-1.5 font-heading text-base font-extrabold text-[#4C1D95]"><Heart size={18} weight="fill" className="text-[#DB2777]" /> Akun Pasien</span>
        </div>
      </header>
      <main className="mx-auto max-w-md px-4 py-8">
        <div data-testid={testid} className="rounded-3xl border border-pink-100 bg-white p-6 shadow-[0_20px_50px_-30px_rgba(124,77,224,0.6)]">
          <h1 className="font-heading text-2xl font-extrabold text-[#4C1D95]">{title}</h1>
          {subtitle && <p className="mt-1 mb-5 text-sm text-slate-500">{subtitle}</p>}
          {children}
        </div>
      </main>
    </div>
  );
}

export default function PatientAuth({ mode }) {
  if (mode === "register") return <Register />;
  if (mode === "verify") return <Verify />;
  if (mode === "forgot") return <Forgot />;
  if (mode === "reset") return <Reset />;
  return <LoginForm />;
}

function LoginForm() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await patientApi.post("/patient/auth/login", { email, password });
      localStorage.setItem("patient_token", data.access_token);
      toast.success("Berhasil masuk");
      nav(returnTo && returnTo.startsWith("/") ? returnTo : "/akun-saya");
    } catch (err) { toast.error(patientErr(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Masuk Pasien" subtitle="Masuk ke akun HalloGBS Anda." testid="patient-login">
      <form onSubmit={submit} className="space-y-4">
        <div><label className="mb-1.5 block text-xs font-bold text-slate-500">Email</label>
          <input data-testid="pl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="email@contoh.com" /></div>
        <div><label className="mb-1.5 block text-xs font-bold text-slate-500">Password</label>
          <input data-testid="pl-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" /></div>
        <button data-testid="pl-submit" disabled={loading} className={btnCls}><LockKey size={18} weight="bold" /> {loading ? "Memproses..." : "Masuk"}</button>
      </form>
      <div className="mt-4 flex items-center justify-between text-sm">
        <Link to="/pasien/lupa-password" data-testid="pl-forgot" className="font-bold text-[#7C4DE0]">Lupa password?</Link>
        <Link to="/pasien/daftar" data-testid="pl-to-register" className="font-bold text-[#DB2777]">Daftar akun</Link>
      </div>
    </Shell>
  );
}

function Register() {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await patientApi.post("/patient/auth/register", f);
      setDone(true);
    } catch (err) { toast.error(patientErr(err)); } finally { setLoading(false); }
  };
  if (done) return (
    <Shell title="Cek Email Anda" testid="patient-register-done">
      <p data-testid="pr-done" className="text-sm text-slate-600">Kami telah mengirim tautan verifikasi ke <b>{f.email}</b>. Buka email dan klik tautan untuk mengaktifkan akun, lalu masuk.</p>
      <Link to="/pasien/masuk" className={`${btnCls} mt-6`}>Ke Halaman Masuk</Link>
    </Shell>
  );
  return (
    <Shell title="Daftar Pasien" subtitle="Buat akun HalloGBS Anda." testid="patient-register">
      <form onSubmit={submit} className="space-y-4">
        <div><label className="mb-1.5 block text-xs font-bold text-slate-500">Nama</label>
          <input data-testid="pr-name" required value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Nama lengkap" /></div>
        <div><label className="mb-1.5 block text-xs font-bold text-slate-500">Email</label>
          <input data-testid="pr-email" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="email@contoh.com" /></div>
        <div><label className="mb-1.5 block text-xs font-bold text-slate-500">Password (min. 6 karakter)</label>
          <input data-testid="pr-password" type="password" required minLength={6} value={f.password} onChange={(e) => set("password", e.target.value)} className={inputCls} placeholder="••••••••" /></div>
        <button data-testid="pr-submit" disabled={loading} className={btnCls}><User size={18} weight="bold" /> {loading ? "Memproses..." : "Daftar"}</button>
      </form>
      <div className="mt-4 text-center text-sm"><Link to="/pasien/masuk" data-testid="pr-to-login" className="font-bold text-[#7C4DE0]">Sudah punya akun? Masuk</Link></div>
    </Shell>
  );
}

function Verify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading|ok|error
  useEffect(() => {
    if (!token) { setState("error"); return; }
    patientApi.post("/patient/auth/verify-email", { token })
      .then(() => setState("ok")).catch(() => setState("error"));
  }, [token]);
  return (
    <Shell title="Verifikasi Email" testid="patient-verify">
      {state === "loading" && <p className="text-sm text-slate-500" data-testid="pv-loading">Memverifikasi…</p>}
      {state === "ok" && <><p data-testid="pv-ok" className="text-sm text-emerald-600">Email berhasil diverifikasi. Silakan masuk.</p><Link to="/pasien/masuk" className={`${btnCls} mt-6`}>Masuk Sekarang</Link></>}
      {state === "error" && <><p data-testid="pv-error" className="text-sm text-rose-600">Tautan verifikasi tidak valid atau sudah kadaluarsa.</p><Link to="/pasien/masuk" className={`${btnCls} mt-6`}>Ke Halaman Masuk</Link></>}
    </Shell>
  );
}

function Forgot() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await patientApi.post("/patient/auth/forgot-password", { email }); setDone(true); }
    catch (err) { toast.error(patientErr(err)); } finally { setLoading(false); }
  };
  if (done) return (
    <Shell title="Cek Email Anda" testid="patient-forgot-done">
      <p data-testid="pf-done" className="text-sm text-slate-600">Jika email terdaftar, kami telah mengirim tautan reset password. Silakan cek email Anda.</p>
      <Link to="/pasien/masuk" className={`${btnCls} mt-6`}>Kembali Masuk</Link>
    </Shell>
  );
  return (
    <Shell title="Lupa Password" subtitle="Masukkan email akun Anda." testid="patient-forgot">
      <form onSubmit={submit} className="space-y-4">
        <div><label className="mb-1.5 block text-xs font-bold text-slate-500">Email</label>
          <input data-testid="pf-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="email@contoh.com" /></div>
        <button data-testid="pf-submit" disabled={loading} className={btnCls}><EnvelopeSimple size={18} weight="bold" /> {loading ? "Mengirim..." : "Kirim Tautan Reset"}</button>
      </form>
      <div className="mt-4 text-center text-sm"><Link to="/pasien/masuk" className="font-bold text-[#7C4DE0]">Kembali Masuk</Link></div>
    </Shell>
  );
}

function Reset() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await patientApi.post("/patient/auth/reset-password", { token, password });
      toast.success("Password diperbarui. Silakan masuk.");
      nav("/pasien/masuk");
    } catch (err) { toast.error(patientErr(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Reset Password" subtitle="Buat password baru Anda." testid="patient-reset">
      <form onSubmit={submit} className="space-y-4">
        <div><label className="mb-1.5 block text-xs font-bold text-slate-500">Password Baru (min. 6 karakter)</label>
          <input data-testid="prs-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" /></div>
        <button data-testid="prs-submit" disabled={loading} className={btnCls}><LockKey size={18} weight="bold" /> {loading ? "Menyimpan..." : "Simpan Password"}</button>
      </form>
    </Shell>
  );
}
